import { useEffect, useMemo, useRef, useState } from "react";

import { LoomAircraftViewer } from "./LoomAircraftViewer";
import { AirshowReviewWorkbench } from "./AirshowReviewWorkbench";
import { LoomBuildWorkbench } from "./LoomBuildWorkbench";
import { LoomPanelLayout } from "./LoomPanelLayout";
import { X57_LOCAL_DEMO_PROJECT_ID } from "./x57VspApi";
import {
  loadX57VspAircraft,
  X57_DEFAULT_VSP_SOURCE,
  X57_VSP_SOURCES,
  type X57ComponentRole,
  type X57EditableParameter,
  type X57VspAircraft,
} from "./x57VspBridge";
import { buildX57SurfaceLines, x57ComponentSurfaceStats } from "./x57SurfaceModel";
import "./parametric-geometry.css";

const VSP_PARAMETER_LABELS: Readonly<Record<string, string>> = {
  Span: "Imported half-span",
  Root_Chord: "Imported root chord",
  Tip_Chord: "Imported tip chord",
  Sweep: "Imported sweep",
  Sweep_Location: "Imported sweep reference",
  Twist: "Imported tip twist",
};

const COMPONENT_LABELS: Readonly<Record<X57ComponentRole, string>> = {
  "main-wing": "Main wing",
  fuselage: "Fuselage",
  empennage: "Empennage",
  nacelle: "Nacelles",
  pylon: "Pylons",
  propeller: "Propellers",
  "actuator-disk": "Actuator disks",
  other: "Other surfaces",
};

const parameterStep = (parameter: X57EditableParameter) => {
  if (parameter.unit === "ratio") return 0.01;
  if (parameter.unit === "deg") return 0.5;
  return parameter.unit === "in" ? 0.1 : 0.01;
};

const formatParameter = (parameter: X57EditableParameter, value: number) => {
  const unit = parameter.unit === "deg" ? "°" : parameter.unit === "ratio" ? "" : ` ${parameter.unit}`;
  return `${value.toFixed(parameter.unit === "ratio" ? 2 : 1)}${unit}`;
};

function Metric({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="pg-metric">
      <span>{label}</span>
      <strong data-testid={testId}>{value}</strong>
    </div>
  );
}

function componentSummary(aircraft: X57VspAircraft | null) {
  if (!aircraft) return [];
  const counts = new Map<X57ComponentRole, number>();
  for (const component of aircraft.components) {
    counts.set(component.role, (counts.get(component.role) ?? 0) + 1);
  }
  return [...counts.entries()].map(([role, count]) => ({ role, count }));
}

interface ParametricGeometrySurfaceProps {
  projectId: string;
  projectName: string;
  branch: string;
  persistedProject: boolean;
}

export function ParametricGeometrySurface(props: ParametricGeometrySurfaceProps) {
  const [mode, setMode] = useState<"build" | "import" | "compare">("build");
  return (
    <div className="pg-product" data-mode={mode}>
      <nav aria-label="Loom workbench mode" className="pg-mode-switch">
        <button
          aria-label="Build"
          aria-pressed={mode === "build"}
          data-testid="loom-mode-native"
          onClick={() => setMode("build")}
          type="button"
        >
          <strong data-testid="loom-mode-build">Build</strong>
        </button>
        <button
          aria-label="Import"
          aria-pressed={mode === "import"}
          data-testid="loom-mode-import"
          onClick={() => setMode("import")}
          type="button"
        >
          <strong>Import</strong>
        </button>
        <button
          aria-label="Compare"
          aria-pressed={mode === "compare"}
          data-testid="loom-mode-compare"
          onClick={() => setMode("compare")}
          type="button"
        >
          <strong data-testid="openvsp-comparison-open">Compare</strong>
        </button>
      </nav>
      {mode === "build" ? (
        <LoomBuildWorkbench {...props} />
      ) : mode === "import" ? (
        <X57ImportWorkbench {...props} />
      ) : (
        <AirshowReviewWorkbench {...props} />
      )}
    </div>
  );
}

function X57ImportWorkbench({
  projectId,
  projectName,
  branch,
  persistedProject,
}: ParametricGeometrySurfaceProps) {
  const [sourceKey, setSourceKey] = useState(X57_DEFAULT_VSP_SOURCE.catalogKey);
  const [parameterOverrides, setParameterOverrides] = useState<Record<string, number>>({});
  const [aircraft, setAircraft] = useState<X57VspAircraft | null>(null);
  const [aircraftLoading, setAircraftLoading] = useState(true);
  const [aircraftError, setAircraftError] = useState<string | null>(null);
  const [selectedGeomId, setSelectedGeomId] = useState<string | null>(null);
  const aircraftLoadAbortRef = useRef<AbortController | null>(null);
  const selectedSource = useMemo(
    () => X57_VSP_SOURCES.find((source) => source.catalogKey === sourceKey) ?? X57_DEFAULT_VSP_SOURCE,
    [sourceKey],
  );
  const components = useMemo(() => componentSummary(aircraft), [aircraft]);
  const surfaceComponents = aircraft?.surfaceModel.components ?? [];
  const selectedSurfaceLines = useMemo(
    () => aircraft ? buildX57SurfaceLines(aircraft.surfaceModel, selectedGeomId) : null,
    [aircraft, selectedGeomId],
  );
  const selectedSurfaceStats = useMemo(
    () => aircraft ? x57ComponentSurfaceStats(aircraft.surfaceModel, selectedGeomId) : null,
    [aircraft, selectedGeomId],
  );
  const vspProjectId = !persistedProject && import.meta.env.DEV
    ? X57_LOCAL_DEMO_PROJECT_ID
    : projectId;

  useEffect(() => {
    aircraftLoadAbortRef.current?.abort();
    const controller = new AbortController();
    aircraftLoadAbortRef.current = controller;
    setAircraft((current) => current?.catalogKey === selectedSource.catalogKey ? current : null);
    setAircraftLoading(true);
    setAircraftError(null);
    const load = loadX57VspAircraft({
      projectId: vspProjectId,
      resolution: 6,
      source: selectedSource,
      parameterOverrides,
      signal: controller.signal,
    });
    load.then((nextAircraft) => {
      if (controller.signal.aborted) return;
      setAircraft(nextAircraft);
      setAircraftLoading(false);
      setSelectedGeomId((current) => nextAircraft.surfaceModel.components.some(
        (component) => component.geomId === current,
      ) ? current : nextAircraft.surfaceModel.components.find(
        (component) => /^(main|clean)\s*wing$/i.test(component.name),
      )?.geomId ?? nextAircraft.surfaceModel.components[0]?.geomId ?? null);
    }).catch((error: unknown) => {
      if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) return;
      setAircraft(null);
      setAircraftLoading(false);
      setAircraftError(error instanceof Error ? error.message : "Unable to import the X-57 source model");
    });
    return () => controller.abort();
  }, [parameterOverrides, selectedSource, vspProjectId]);

  useEffect(() => () => aircraftLoadAbortRef.current?.abort(), []);

  const updateSourceParameter = (parameter: X57EditableParameter, value: number) => {
    setParameterOverrides((current) => ({ ...current, [parameter.id]: value }));
  };

  const changeSource = (catalogKey: string) => {
    setParameterOverrides({});
    setSelectedGeomId(null);
    setSourceKey(catalogKey);
  };

  return (
    <main className="pg-workbench" data-testid="parametric-geometry-workbench">
      <header className="pg-header">
        <div>
          <span className="pg-eyebrow">Loom · import adapter</span>
          <h1>Import an X-57 into Loom</h1>
          <p>Inspect source surfaces and conversion status. OpenVSP supplies input; Loom remains the geometry engine.</p>
        </div>
        <div className="pg-context" aria-label="Thread context">
          <span>{projectName}</span>
          <code>{branch}</code>
        </div>
      </header>

      <LoomPanelLayout storageId="import">
        <aside className="pg-panel pg-controls" aria-label="Imported X-57 source parameters">
          <div className="pg-panel-heading">
            <div>
              <span>Import source</span>
              <strong data-testid="loom-model-name">NASA X-57 Maxwell</strong>
            </div>
            <button type="button" onClick={() => setParameterOverrides({})}>Reset</button>
          </div>
          <div className="pg-model-picker">
            <label htmlFor="loom-vsp-model">OpenVSP model</label>
            <select
              aria-label="OpenVSP model"
              id="loom-vsp-model"
              onChange={(event) => changeSource(event.target.value)}
              value={sourceKey}
            >
              {X57_VSP_SOURCES.map((source) => (
                <option key={source.catalogKey} value={source.catalogKey}>{source.label}</option>
              ))}
            </select>
            <span data-testid="loom-vsp-source">
              {selectedSource.fileName}
            </span>
          </div>
          <div className="pg-component-list" data-testid="loom-component-list">
            {components.map(({ role, count }) => (
              <div data-testid="loom-component" key={role}>
                <span>{COMPONENT_LABELS[role]}</span><strong>× {count}</strong>
              </div>
            ))}
            {aircraftError ? <p className="pg-inline-error" role="alert">{aircraftError}</p> : null}
            {aircraftLoading ? <p className="pg-loading">Reading source surfaces…</p> : null}
          </div>
          <div className="pg-control-list">
            {aircraft?.editableParameters.map((parameter) => {
              const value = parameterOverrides[parameter.id] ?? parameter.value;
              const label = VSP_PARAMETER_LABELS[parameter.name] ?? `Imported ${parameter.name}`;
              return (
                <label className="pg-control" key={parameter.id}>
                  <span>
                    <b>{label}</b>
                    <output data-testid={`parametric-output-vsp-${parameter.name}`}>
                      {formatParameter(parameter, value)}
                    </output>
                  </span>
                  <input
                    aria-label={label}
                    data-parm-id={parameter.id}
                    data-testid={`parametric-control-vsp-${parameter.name}`}
                    max={parameter.uiMax}
                    min={parameter.uiMin}
                    onChange={(event) => updateSourceParameter(parameter, Number(event.target.value))}
                    step={parameterStep(parameter)}
                    type="range"
                    value={value}
                  />
                </label>
              );
            })}
          </div>
          <div className="pg-definition-note">
            <strong>Adapter boundary</strong>
            <span>These controls modify the source model. A successful import does not imply that its components have been Boolean-trimmed into one Loom body.</span>
          </div>
        </aside>

        <section className="pg-panel pg-stage" aria-label="Imported X-57 parameter surfaces">
          <div className="pg-stage-bar">
            <div>
              <span className="pg-status is-ready" data-testid="loom-import-status">
                <i />{aircraft ? "Source surfaces loaded" : "Loading source surfaces"}
              </span>
              <small>{aircraft?.build.cache_hit ? "Import surface cache hit" : "Canonical project-scoped import"}</small>
            </div>
            <div className="pg-cp-legend" aria-label="Surface construction legend">
              <span>u/v samples</span><i /><span>surface boundary</span>
            </div>
          </div>
          {aircraft ? (
            <LoomAircraftViewer
              aircraft={aircraft}
              selectedSurfaceComponentId={selectedGeomId}
              surfaceLines={selectedSurfaceLines}
            />
          ) : (
            <div className="pg-stage-empty">
              <strong>{aircraftError ? "X-57 import failed" : "Loading the X-57 source assembly"}</strong>
              <span>{aircraftError ?? "Reading component geometry and sampled source surfaces…"}</span>
            </div>
          )}
          <footer className="pg-stage-footer">
            <span>Drag to orbit · blue = sampled u/v lines · amber = surface boundary</span>
            <code>{aircraft ? `${aircraft.surfaceMetadata.surfaceOrPatchCount} source surfaces` : "source adapter"}</code>
          </footer>
        </section>

        <aside className="pg-panel pg-results" aria-label="Loom import conversion status">
          <div className="pg-panel-heading">
            <div>
              <span>Representation boundary</span>
              <strong>Source → Loom</strong>
            </div>
            <span className="pg-live is-idle"><i />inspect</span>
          </div>
          <div className="pg-model-picker">
            <label htmlFor="loom-surface-component">Inspect imported component</label>
            <select
              data-testid="loom-surface-component"
              id="loom-surface-component"
              onChange={(event) => setSelectedGeomId(event.target.value)}
              value={selectedGeomId ?? ""}
            >
              {surfaceComponents.map((component) => (
                <option key={component.geomId} value={component.geomId}>{component.name}</option>
              ))}
            </select>
          </div>
          <div className="pg-solver-status is-solved" data-testid="loom-surface-status">
            <strong>{selectedSurfaceStats?.component?.name ?? "Select a source component"}</strong>
            <span>{aircraft?.surfaceMetadata.mode === "sampled-grid"
              ? "VSP sampled parameter grid — not an analytic control net"
              : "Exact parametric patch data"}</span>
          </div>
          <div className="pg-metrics">
            <Metric label="Components" value={aircraft ? String(aircraft.surfaceMetadata.componentCount) : "—"} />
            <Metric label="Source surfaces" value={aircraft ? String(aircraft.surfaceMetadata.surfaceOrPatchCount) : "—"} />
            <Metric label="Selected surfaces" value={selectedSurfaceStats ? String(selectedSurfaceStats.surfaceCount) : "—"} />
            <Metric label="Selected samples" value={selectedSurfaceStats ? selectedSurfaceStats.points.toLocaleString() : "—"} />
            <Metric label="u/v segments" value={selectedSurfaceLines ? selectedSurfaceLines.parameterLineCount.toLocaleString() : "—"} />
            <Metric label="Display triangles" value={aircraft ? aircraft.surfaceMetadata.triangleCount.toLocaleString() : "—"} />
          </div>
          <div className="pg-solver-note">
            <span>Import is not the engine</span>
            <code>source → Loom fields/surfaces → hybridization → conforming mesh</code>
            <p>This proves source ingestion and inspection. The X-57 has not yet passed Loom’s shared-seam Boolean and watertight-mesh gates.</p>
          </div>
          <div className="pg-record-card">
            <strong>Downstream panel method gated</strong>
            <p>No Cp or wake is shown. Aero unlocks only after the imported components become one conforming Loom body with explicit trailing-edge ordering.</p>
            <button disabled type="button">Run panel method</button>
            <small>Boolean-trimmed X-57: not yet established</small>
          </div>
        </aside>
      </LoomPanelLayout>
    </main>
  );
}

export default ParametricGeometrySurface;
